import { useState, useEffect } from "react";
import { GitBranch, Network, Globe } from "lucide-react";
import DownwardTreeView from "./DownwardTreeView";
import UpwardTreeView from "./UpwardTreeView";
import { ConsumerNetworkView } from "./ConsumerNetworkView";
import { Notification } from "./Notification";
import { UpstreamNetworkView } from "./UpstreamNetworkView";
import heartLoading from "../../../assests/heartLoading.gif";
import axios from "axios";
import { Tooltip } from "@mui/material";

export const DataContractDetails = ({ contract }) => {
  const [viewMode, setViewMode] = useState("network");
  const [renderType, setRenderType] = useState("downstream");
  // Check if current user is the product owner or an admin
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");
  const isProductOwner = contract.productOwner === userId;
  const isAdmin = userRole === "Admin";
  const canSendNotification = isProductOwner || isAdmin;
  const [consumerData, setConsumerData] = useState();
  const [loading, setLoading] = useState(true);

  // Fetch cosnsumer dependencies and email list
  useEffect(() => {
    const fetchConsumerData = async () => {
      if (!contract?.id) {
        setLoading(false);
        return;
      };

      if(!loading){
        setLoading(true);
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-consumer-dependencies/${contract?.id}`,
        );
        setConsumerData(response.data);
      } catch (err) {
        console.error("Failed to fetch consumer data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConsumerData();
  }, [contract?.id]);

  return (
    <>
      {loading ? (
        <div className="relative bg-white flex items-center justify-center z-50 min-h-[80vh]">
          <div className="flex flex-col items-center space-y-3">
            <img src={heartLoading} alt="Loading..." className="h-30" />
            <span className="text-gray-700 text-[20px] text-center">
              Loading consumer dependencies...
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between-items item-start px-4 py-3 gap-6">
            <p className="text-gray-600 flex-1">{contract.description}</p>
            <div className="bg-white border border-gray-300 rounded-lg p-2 shadow-sm">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Legend
              </div>
              <div className="space-y-1">
                <div className="flex items-center text-xs">
                  <span className="text-blue-600">Data Contracts</span>
                </div>
                <div className="flex items-center gap-2 text-xs leading-none  cursor-pointer">
                  <Tooltip
                    title="All the rules of the dependent parent asset have passed."
                    PopperProps={{
                      sx: { zIndex: 999999999 },
                    }}
                  >
                    <div className="flex items-center gap-1  cursor-pointer ">
                      <Globe
                        className="w-8 h-8 text-white block stroke-green-500"
                        title="All the rules of the dependent parent asset have passed."
                      />
                    </div>
                  </Tooltip>

                  <Tooltip
                    title="One or more rules of the dependent parent asset has failed"
                    PopperProps={{
                      sx: { zIndex: 999999999 },
                    }}
                  >
                    <div className="flex items-center gap-1  cursor-pointer">
                      <Globe className="w-8 h-8 text-white block stroke-[#FFBF00]" />
                    </div>
                  </Tooltip>
                  <Tooltip
                    title="One or more dimensions of the dependent parent asset has failed."
                    PopperProps={{
                      sx: { zIndex: 999999999 },
                    }}
                  >
                    <div className="flex items-center gap-1  cursor-pointer">
                      <Globe className="w-8 h-8 text-white block stroke-red-500" />
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          <div className="p-3 space-y-4">
            <div className="space-y-6">
              <div className="bg-[#F2F9F7] rounded-xl p-3">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-[16px] font-semibold text-gray-900 flex items-center gap-2">
                      <GitBranch className=" h-5 text-blue-600" />
                      Consumer Dependencies
                    </h3>
                    <h4>
                      The color of each product represents its overall health —
                      based on the combined health of its parent assets if
                      subscribed, or its own asset health if not subscribed
                    </h4>
                  </div>
                  <div className="flex flex-column">
                    <div className="flex justify-content-center bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
                      <button
                        onClick={() => setViewMode("network")}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-1 ${
                          viewMode === "network"
                            ? "bg-blue-100 text-blue-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <Network className="w-4 h-4" />
                        Network
                      </button>
                      <button
                        onClick={() => setViewMode("tree")}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-1 ${
                          viewMode === "tree"
                            ? "bg-blue-100 text-blue-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <GitBranch className="w-4 h-4" />
                        Tree
                      </button>
                    </div>
                    <div className="flex bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
                      <button
                        onClick={() => setRenderType("downstream")}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-1 ${
                          renderType === "downstream"
                            ? "bg-blue-100 text-blue-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        Downstream
                      </button>
                      <button
                        onClick={() => setRenderType("upstream")}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-1 ${
                          renderType === "upstream"
                            ? "bg-blue-100 text-blue-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        Upstream
                      </button>
                    </div>
                  </div>
                </div>
                <div className="transition-all duration-300">
                  {viewMode === "tree" ? (
                    <>
                      {renderType === "upstream" ? (
                        <UpwardTreeView
                          contractName={contract.name}
                          contractStatus={consumerData?.subscription_status || contract.status}
                          dependencies={consumerData?.upstreamDependencies}
                        />
                      ) : (
                        <DownwardTreeView
                          contractName={contract.name}
                          contractStatus={contract.status}
                          dependencies={consumerData?.consumerDependencies}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {renderType === "upstream" ? (
                        <UpstreamNetworkView
                          consumers={consumerData?.upstreamDependencies || []}
                          contractName={contract.name}
                          contractStatus={consumerData?.subscription_status || contract.status}
                        />
                      ) : (
                        <ConsumerNetworkView
                          consumers={consumerData?.consumerDependencies || []}
                          contractName={contract.name}
                          contractStatus={contract.status}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6"></div>
            </div>
          </div>
          {canSendNotification && (
            <div className="p-3">
              <Notification
                productId={contract?.id}
                emailList={consumerData?.emails || []}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
